import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, ease as mmEase, useInView } from "@/lib/motion";
import { LIVE_FEED } from "../data";

const ez = mmEase.expo;


/**
 * The Witness: a 120px micro Thread that plays once per live entry on mount.
 * Color inherits from the entry's corridor color.
 */
function WitnessThread({ color }) {
  const id = useRef(`wt-${Math.random().toString(36).slice(2)}`).current;
  return (
    <svg
      viewBox="0 0 120 14"
      fill="none"
      aria-hidden="true"
      style={{ width: "120px", height: "14px", flexShrink: 0, overflow: "visible" }}
    >
      <defs>
        <filter id={`wtG-${id}`} x="-60%" y="-200%" width="220%" height="500%">
          <feGaussianBlur stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <linearGradient id={`wtGrad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor={color} stopOpacity="0.15" />
          <stop offset="50%"  stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <path
        id={`wt-path-${id}`}
        d="M 0 7 C 30 2, 60 12, 90 7 S 112 3, 120 7"
        stroke={`url(#wtGrad-${id})`}
        strokeWidth="1"
        strokeLinecap="round"
        fill="none"
        strokeDasharray="4 3"
        opacity="0.6"
        style={{ animation: "dashMove 1s linear infinite" }}
      />
      {/* Packet plays once — 1.5s duration, then stops */}
      <circle r="3.5" fill={color} opacity="0.18">
        <animateMotion dur="1.5s" repeatCount="1" fill="freeze">
          <mpath href={`#wt-path-${id}`} />
        </animateMotion>
        <animate attributeName="opacity" values="0;0.18;0" keyTimes="0;0.5;1" dur="1.5s" repeatCount="1" fill="freeze" />
      </circle>
      <circle r="2.2" fill={color} filter={`url(#wtG-${id})`}>
        <animateMotion dur="1.5s" repeatCount="1" fill="freeze">
          <mpath href={`#wt-path-${id}`} />
        </animateMotion>
        <animate attributeName="opacity" values="0;1;0" keyTimes="0;0.6;1" dur="1.5s" repeatCount="1" fill="freeze" />
      </circle>
    </svg>
  );
}

export default function LiveProofSection() {
  const [items, setItems] = useState(() => LIVE_FEED.slice(0, 4));
  const nextRef = useRef(4);
  const ref     = useRef(null);
  const inView  = useInView(ref, { once: true, margin: "-80px 0px" });

  useEffect(() => {
    const t = setInterval(() => {
      const next = LIVE_FEED[nextRef.current % LIVE_FEED.length];
      nextRef.current += 1;
      setItems((prev) => [next, ...prev.slice(0, 3)]);
    }, 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} className="py-24 px-6" style={{ background: "var(--color-footer-bg)" }}>
      <div className="max-w-[1200px] mx-auto">
        <div className="grid lg:grid-cols-[1fr_420px] gap-12 xl:gap-20 items-center">

          {/* Left */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease: ez }}
              className="flex items-center gap-2.5 mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald">
                Live transfers
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
              animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.72, ease: ez, delay: 0.08 }}
              className="font-display font-black text-white leading-[1.08] mb-6"
              style={{ fontSize: "clamp(1.9rem, 4vw, 3.4rem)", letterSpacing: "-0.035em" }}
            >
              Transfers happening<br />
              <span style={{ color: "rgba(255,255,255,0.26)" }}>right now.</span>
            </motion.h2>


            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, ease: ez, delay: 0.18 }}
            >
              <p
                className="font-display font-black text-white num-tabular"
                style={{ fontSize: "clamp(2.6rem, 5vw, 3.8rem)", letterSpacing: "-0.045em", lineHeight: 1 }}
              >
                $47.8M
              </p>
              <p className="text-[11px] font-medium mt-2" style={{ color: "rgba(255,255,255,0.34)" }}>
                Transferred today
              </p>
            </motion.div>

            {/* Photograph — the moment money arrives */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.72, ease: ez, delay: 0.28 }}
              style={{ marginTop: "28px", borderRadius: "16px", overflow: "hidden", maxWidth: "100%" }}
            >
              <img
                src="https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=640&q=80&auto=format&fit=crop"
                alt="Woman smiling at her phone — the moment the transfer arrives"
                style={{ width: "100%", height: "200px", objectFit: "cover", objectPosition: "center top", display: "block" }}
              />
            </motion.div>
          </div>

          {/* Right — live feed with The Witness Threads */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, ease: ez, delay: 0.2 }}
            className="rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
          >
            <div
              className="px-5 py-3.5 flex items-center gap-2"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald animate-pulse shrink-0" />
              <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.22)" }}>
                Updates in real time
              </span>
            </div>

            <div className="p-4 space-y-2" style={{ minHeight: "256px" }}>
              <AnimatePresence mode="popLayout">
                {items.map((item) => (
                  <motion.div
                    key={`${item.id}-${item.name}`}
                    layout
                    initial={{ opacity: 0, y: -14, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0,   scale: 1    }}
                    exit={{    opacity: 0, y:  14, scale: 0.97 }}
                    transition={{ duration: 0.28, ease: ez }}
                    className="px-3.5 py-3 rounded-xl"
                    style={{ background: "rgba(255,255,255,0.04)" }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-7 h-7 rounded-full grid place-items-center text-[9px] font-bold shrink-0"
                        style={{ background: `${item.color}20`, color: item.color }}
                      >
                        {item.from}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-[12px] font-semibold truncate"
                          style={{ color: "rgba(255,255,255,0.70)" }}
                        >
                          {item.name}
                        </p>
                        <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.28)" }}>
                          {item.from} → {item.to} · {item.dest}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p
                          className="text-[12px] font-bold num-tabular"
                          style={{ color: "rgba(255,255,255,0.80)" }}
                        >
                          {item.amount}
                        </p>
                        <p className="text-[10px]" style={{ color: item.color }}>✓ sent</p>
                      </div>
                    </div>
                    {/* The Witness: micro Thread plays once on entry mount */}
                    <div style={{ marginTop: "6px", paddingLeft: "10px" }}>
                      <WitnessThread color={item.color} />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div
              className="px-5 py-3 text-center"
              style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
            >
              <p className="text-[10px]" style={{ color: "rgba(255,255,255,0.18)" }}>
                Data reflects real transfer activity
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
